import { SearchAvailableRoom } from "@/components/organisms/search-avaliable/SearchAvailableRoomMobile";
import { createAppStore } from "../index";
import {
  ConfigurationSearchFormQuery,
  ConfigurationSearchFormQueryVariables,
} from "@/types/generated";
import { fetchCMS } from "@/graphql/client/client";

import { SEARCH_AVAILABLE_ROOM_CONFIGURATIONS } from "@/queries/searchAvailableRoomQueries";
import { DEFAULT_LOCALE } from "@/i18n/routing";
import { createJSONStorage, StateStorage } from "zustand/middleware";
import { IGetRoomStatusByDatesResponse } from "@app/api/tripla/roomStatusByDates";
import { DEFAULT_CHECKIN_DATE, DEFAULT_CHECKOUT_DATE } from "@/constants";
import {
  checkAndValidateStoredDates,
  formatDateForStorage,
  parseStoredDate,
} from "@/utils";

interface StorageItem {
  value: string;
  expiry: number;
}
const SEARCH_FORM_STORAGE_KEY = "mystay_search_form";

export enum FilterRoomModalStateEnum {
  OPEN, // Modal is open.
  SUBMITED, // Modal is close after submit.
}
const createLocalStorageWithExpiry = (expiryDays: number = 1): StateStorage => {
  const expiryMs = expiryDays * 24 * 60 * 60 * 1000;

  return {
    getItem: (name: string): string | null => {
      // Check if we're running in the browser
      if (globalThis.window === undefined) {
        return null;
      }

      try {
        const itemStr = localStorage.getItem(name);
        if (!itemStr) {
          return null;
        }

        const item: StorageItem = JSON.parse(itemStr);
        const now = Date.now();

        if (now > item.expiry) {
          localStorage.removeItem(name);
          return null;
        }

        return item.value;
      } catch (error) {
        console.error("Error reading from localStorage:", error);
        return null;
      }
    },

    setItem: (name: string, value: string): void => {
      // Check if we're running in the browser
      if (globalThis.window === undefined) {
        return;
      }

      try {
        const now = Date.now();
        const item: StorageItem = {
          value,
          expiry: now + expiryMs,
        };
        localStorage.setItem(name, JSON.stringify(item));
      } catch (error) {
        console.error("Error writing to localStorage:", error);
      }
    },

    removeItem: (name: string): void => {
      // Check if we're running in the browser
      if (globalThis.window === undefined) {
        return;
      }

      try {
        localStorage.removeItem(name);
      } catch (error) {
        console.error("Error removing from localStorage:", error);
      }
    },
  };
};

const normalizeSearchFormDates = (
  formData: SearchAvailableRoom | null,
): SearchAvailableRoom | null => {
  if (!formData?.checkIn && !formData?.checkout) {
    return formData;
  }

  const { checkIn, checkOut } = checkAndValidateStoredDates(
    formData.checkIn,
    DEFAULT_CHECKIN_DATE,
    formData.checkout,
    DEFAULT_CHECKOUT_DATE,
  );
  const normalizedCheckIn = formatDateForStorage(
    checkIn,
    DEFAULT_CHECKIN_DATE,
  );
  const normalizedCheckout = formatDateForStorage(
    checkOut,
    DEFAULT_CHECKOUT_DATE,
  );
  const currentCheckIn = formatDateForStorage(
    parseStoredDate(formData.checkIn, DEFAULT_CHECKIN_DATE),
    DEFAULT_CHECKIN_DATE,
  );
  const currentCheckout = formatDateForStorage(
    parseStoredDate(formData.checkout, DEFAULT_CHECKOUT_DATE),
    DEFAULT_CHECKOUT_DATE,
  );

  if (
    currentCheckIn === normalizedCheckIn &&
    currentCheckout === normalizedCheckout
  ) {
    return formData;
  }

  return {
    ...formData,
    checkIn: normalizedCheckIn,
    checkout: normalizedCheckout,
  } as unknown as SearchAvailableRoom;
};

export interface SearchFormState {
  formData: SearchAvailableRoom | null;
  configurations: ConfigurationSearchFormQuery | null;
  configurationsLocale: string | null;
  defaultHotel: { id: string; slug: string } | null;
  filterRoomModalState: FilterRoomModalStateEnum;
  roomAvailability: {
    available: Date[];
    fewRemain: Date[];
    noVacancy: Date[];
  };
  fetchConfigurationsData: (
    locale: string,
    forceDefaultLocale?: boolean,
    targetLocale?: string,
  ) => Promise<void>;
  clearConfigurationsData: () => void;
  setFormData: (data: SearchAvailableRoom) => void;
  clearFormData: () => void;
  setDefaultHotel: (hotel: { id: string; slug: string }) => void;
  setFilterRoomModalState: (state: FilterRoomModalStateEnum) => void;
  setRoomAvailability: (data: IGetRoomStatusByDatesResponse) => void;
}

// Store for managing the search form state including room availability, default hotel, and CMS configurations.
const useSearchFormStore = createAppStore<SearchFormState>(
  (set) => ({
    // formData starts null on the server / before hydration.
    // The validated value is set by onRehydrateStorage below, once
    // persist finishes reading from localStorage on the client.
    formData: null,
    setFormData: (data) =>
      set((state) => {
        state.formData = normalizeSearchFormDates(data);
      }),
    clearFormData: () =>
      set((state) => {
        state.formData = null;
      }),
    configurations: null,
    configurationsLocale: null,
    fetchConfigurationsData: async (locale, _forceDefaultLocale, targetLocale) => {
      try {
        const resolvedLocale = targetLocale || locale || DEFAULT_LOCALE;
        const data = await fetchCMS<ConfigurationSearchFormQuery>(
          SEARCH_AVAILABLE_ROOM_CONFIGURATIONS,
          {
            locale: resolvedLocale,
          } as ConfigurationSearchFormQueryVariables,
          resolvedLocale,
        );

        set((state) => {
          state.configurations = data;
          state.configurationsLocale = resolvedLocale;
        });

        return;
      } catch (error) {
        console.error("Error fetching configurations data:", error);
        throw error;
      }
    },
    clearConfigurationsData: () =>
      set((state) => {
        state.configurations = null;
        state.configurationsLocale = null;
      }),
    defaultHotel: null,
    setDefaultHotel: (hotel) =>
      set((state) => {
        state.defaultHotel = hotel;
      }),
    filterRoomModalState: FilterRoomModalStateEnum.SUBMITED,
    setFilterRoomModalState: (status) =>
      set((state) => {
        state.filterRoomModalState = status;
      }),
    roomAvailability: {
      available: [],
      fewRemain: [],
      noVacancy: [],
    },
    setRoomAvailability: (data) =>
      set((state) => {
        const noVacancy = data.disabled_dates.map((d) => new Date(d)) || [];
        const fewRemain: Date[] = [];
        const available: Date[] = [];

        for (const [dateStr, countStr] of Object.entries(data.inventory)) {
          const count = Number.parseInt(countStr, 10);
          const [year, month, day] = dateStr.split("/").map(Number);
          const date = new Date(year, month - 1, day);

          // Number of room under 10 will be treated as fewRemain.
          if (count < 10) {
            fewRemain.push(date);
          } else {
            available.push(date);
          }
        }

        state.roomAvailability = {
          fewRemain,
          noVacancy,
          available,
        };
      }),
  }),
  SEARCH_FORM_STORAGE_KEY,
  // Persist store in localStorage with 12-hour TTL
  createJSONStorage(() => createLocalStorageWithExpiry(0.5)),
  // Runs once, right after persist hydrates from localStorage on the client.
  // Validates/normalizes checkIn/checkout dates and corrects state (and,
  // via persist's own setItem, the underlying localStorage value) in place.
  () => (state, error) => {
    if (error) {
      console.error("Error rehydrating search form store:", error);
      return;
    }
    if (!state?.formData) return;

    const normalized = normalizeSearchFormDates(state.formData);
    if (normalized !== state.formData) {
      state.formData = normalized;
    }
  },
);

export default useSearchFormStore;
